import { getThemePreference, setThemePreference } from './theme/theme.js';
import type { ThemePref } from './theme/theme.js';

/**
 * UI reimagine (Task 2, shared navbar): the four top-level destinations the
 * navbar links between. "practice" is the SPA itself (`/`); the other three
 * are the generated static content pages (`scripts/gen-content/*`). This type
 * is intentionally duplicated (not imported) between here and
 * `scripts/gen-content/skeleton.ts`'s own `ActiveNav` — the two live in
 * separate build graphs (this file ships in the browser bundle; skeleton.ts
 * runs as a Node build script) and the values must simply match by hand, the
 * same convention `content.css` already uses to mirror `tokens.css`.
 */
export type ActiveNav = 'practice' | 'ragas' | 'ear-training' | 'learn';

const NAV_ITEMS: { nav: ActiveNav; href: string; label: string }[] = [
  { nav: 'practice', href: '/', label: 'Practice' },
  { nav: 'ragas', href: '/ragas/', label: 'Ragas' },
  { nav: 'ear-training', href: '/ear-training/', label: 'Ear training' },
  { nav: 'learn', href: '/learn/', label: 'Learn' },
];

const THEME_OPTIONS: { pref: ThemePref; label: string }[] = [
  { pref: 'auto', label: 'Auto' },
  { pref: 'dark', label: 'Dark' },
  { pref: 'light', label: 'Light' },
];

/**
 * Coherent-IA Task 1: the SPA-only click interception for the navbar's
 * Practice/Ear-training links — passed by `app.ts`'s `mountApp` (the ONLY
 * caller that ever supplies this; `scripts/gen-content/skeleton.ts`'s own
 * `renderHeader()` renders the identical markup for the static content
 * pages, but as plain server-rendered HTML with no equivalent JS module to
 * wire this from — those links stay real, full-page navigations, which is
 * exactly right for them: there's no in-app surface on a content page to
 * "operate" instead). Ragas/Learn are deliberately NOT interceptable here —
 * neither has an in-SPA surface either (both are the static content pages),
 * so clicking them is always meant to be a real navigation, on every page.
 */
export interface NavbarActions {
  /** "Practice" clicked from inside the SPA itself: exit whichever mode
   * (drill/ear) is active and bring the tool back into view, instead of the
   * browser reloading `/` from scratch. */
  onPracticeClick: () => void;
  /** "Ear training" clicked from inside the SPA itself: open ear-training
   * mode in place, instead of navigating to the static `/ear-training/`
   * explainer page. */
  onEarTrainingClick: () => void;
  /** The wordmark clicked from inside the SPA: return to the landing view
   * (data-view="landing") in place, instead of reloading `/`. */
  onHomeClick: () => void;
}

/**
 * Renders + wires the persistent top navbar: the wordmark (links home, not a
 * heading — the page's one `<h1>` lives elsewhere, see index.html's own
 * comment), the four `Practice · Ragas · Ear training · Learn` links (real
 * `<a>`s, so this doubles as real inter-page navigation, not just the SPA's
 * own chrome), and the Auto/Dark/Light theme control. `activeNav` is always
 * `'practice'` for the SPA (there is no router — see `mountApp`'s own doc
 * comment — this page never navigates away from itself), marked via
 * `aria-current="page"` rather than a bespoke "active" class so the styling
 * hook (`.navbar-link[aria-current='page']` in styles.css) and the
 * accessible semantics are the same attribute.
 *
 * `scripts/gen-content/skeleton.ts`'s `renderHeader()` emits the SAME
 * markup/class names (by hand, not by import — see this module's `ActiveNav`
 * doc) for the static content pages, so the navbar reads identically on both
 * sides of the SPA/content-page split; only the theme control's WIRING
 * differs (this mounts a real click listener here, the static pages get a
 * small inline `<script>` doing the equivalent — see `NAVBAR_THEME_SCRIPT`).
 *
 * `actions` (coherent-IA Task 1, see `NavbarActions`'s own doc), when given,
 * intercepts clicks on Practice/Ear training with `preventDefault()` and
 * routes them through the SPA's own mode-switching instead of a real
 * navigation. The real `href`s on those two links are NEVER touched (no
 * attribute removed/rewritten) — a no-JS visitor, a crawler, or a
 * middle-click/open-in-new-tab all still get the plain, correct navigation;
 * this is a pure JS-layer, additive enhancement on top of it.
 */
export function mountNavbar(root: HTMLElement, activeNav: ActiveNav, actions?: NavbarActions): void {
  root.innerHTML = `
    <div class="navbar-inner">
      <a class="wordmark" href="/">swara<b>sense</b></a>
      <nav class="navbar-nav" aria-label="Main">
        ${NAV_ITEMS.map(
          (item) =>
            `<a class="navbar-link" href="${item.href}" data-nav="${item.nav}"${
              item.nav === activeNav ? ' aria-current="page"' : ''
            }>${item.label}</a>`,
        ).join('')}
      </nav>
      <div class="navbar-actions">
        <div class="seg" role="group" aria-label="Theme">
          ${THEME_OPTIONS.map(
            ({ pref, label }) =>
              `<button type="button" data-pref="${pref}"${pref === getThemePreference() ? ' class="on"' : ''}>${label.toUpperCase()}</button>`,
          ).join('')}
        </div>
      </div>
    </div>`;

  // Never aborted: the navbar is mounted exactly once for the page's whole
  // lifetime (no screen to unmount it on, mirroring every other page-lifetime
  // listener in this app — see `mountApp`'s own doc comment), so there's
  // nothing to clean up.
  const { signal } = new AbortController();
  for (const btn of root.querySelectorAll<HTMLButtonElement>('.seg button')) {
    btn.addEventListener(
      'click',
      () => {
        const pref = btn.dataset.pref as ThemePref;
        setThemePreference(pref);
        for (const b of root.querySelectorAll<HTMLButtonElement>('.seg button')) {
          b.classList.toggle('on', b === btn);
        }
      },
      { signal },
    );
  }

  // Coherent-IA Task 1: SPA-only nav interception (see `NavbarActions`'s own
  // doc for why this is opt-in via `actions`, and why only these two links).
  // `preventDefault()` stops the real navigation; the `href`s themselves are
  // left exactly as rendered above.
  // Let the browser handle modifier/middle clicks natively (Cmd/Ctrl/Shift/Alt
  // click + non-primary button = "open in new tab/window") — only intercept a
  // plain left click as the in-app action.
  const isPlainClick = (e: MouseEvent): boolean =>
    e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  if (actions) {
    root
      .querySelector<HTMLAnchorElement>('a.wordmark')
      ?.addEventListener(
        'click',
        (e) => {
          if (!isPlainClick(e)) return;
          e.preventDefault();
          actions.onHomeClick();
        },
        { signal },
      );
    root
      .querySelector<HTMLAnchorElement>('a[data-nav="practice"]')
      ?.addEventListener(
        'click',
        (e) => {
          if (!isPlainClick(e)) return;
          e.preventDefault();
          actions.onPracticeClick();
        },
        { signal },
      );
    root
      .querySelector<HTMLAnchorElement>('a[data-nav="ear-training"]')
      ?.addEventListener(
        'click',
        (e) => {
          if (!isPlainClick(e)) return;
          e.preventDefault();
          actions.onEarTrainingClick();
        },
        { signal },
      );
  }
}
